import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArchiveRestore, RotateCcw, ChevronRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function ArchivedPage() {
  const { readOnly } = useAuth();
  const [projects, setProjects] = useState(null);

  const load = () => api.get("/projects?archived=true").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const unarchive = async (p) => {
    try {
      await api.post(`/projects/${p.id}/unarchive`);
      toast.success(`Project ${p.project_number} restored from archive`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="archived-page" className="bdx-page-enter">
      <Breadcrumbs items={[{ label: "Dashboard", to: "/" }, { label: "Archived Projects" }]} />
      <div className="border-b border-gray-300 pb-7">
        <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase text-[#604184]" style={{ letterSpacing: "0.15em" }}>
          <ArchiveRestore size={15} strokeWidth={1.9} /> BDX Project Archive
        </div>
        <h1 className="font-heading text-3xl font-bold text-gray-950 sm:text-4xl">Archived Projects</h1>
        <p className="mt-2 text-sm text-gray-500">Archived projects remain fully accessible and can be restored at any time</p>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
        <div className="bdx-panel-header">
          <div>
            <h2 className="font-heading text-base font-bold text-gray-950">Completed / Archived Register</h2>
            <p data-testid="archived-record-count" className="mt-0.5 text-xs text-gray-500">{projects === null ? "Loading records" : `${projects.length} ${projects.length === 1 ? "project" : "projects"}`}</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="bdx-table" data-testid="archived-table">
            <thead><tr><th>Project No.</th><th>Type</th><th>Address</th><th>Dwellings</th><th>Status</th><th>Created</th>{!readOnly && <th className="w-36">Actions</th>}</tr></thead>
            <tbody>
              {projects?.length === 0 && <tr><td colSpan={7} className="py-14 text-center text-gray-400">No archived projects</td></tr>}
              {projects?.map((p) => (
                <tr key={p.id}>
                  <td><Link data-testid={`archived-project-${p.project_number}`} to={`/projects/${p.id}`} className="inline-flex min-w-12 items-center justify-center rounded-md border border-[#d9cde5] bg-[#f3eff7] px-2.5 py-1.5 font-mono-data text-xs font-semibold text-[#604184]">{p.display_reference || p.project_number}</Link></td>
                  <td><span className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600">{p.project_type}</span></td>
                  <td><Link data-testid={`archived-project-address-${p.project_number}`} to={`/projects/${p.id}`} className="group flex min-w-[240px] items-center justify-between gap-4 font-semibold text-gray-950"><span>{p.label}</span><ChevronRight size={15} className="text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[#604184]" /></Link></td>
                  <td className="font-mono-data">{p.dwellings_connected} of {p.dwellings_total}</td>
                  <td><StatusBadge status="Archived" tone="grey" /></td>
                  <td className="font-mono-data text-xs text-gray-500">{fmtDate(p.created_at)}</td>
                  {!readOnly && (
                    <td><button data-testid={`unarchive-${p.project_number}`} className="bdx-btn-secondary text-xs" onClick={() => unarchive(p)}><RotateCcw size={14} strokeWidth={1.8} /> Restore</button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}